import _ from 'lodash';

import {
  EntityTypes,
  GqlBankAccount,
  GqlBankDetails,
  GqlBillingAddress,
  Maybe,
  StatusTypes,
  useGetEnrollmentStatusQuery,
} from 'types/graphql';
import useDispensaryPaymentMethods from 'shared/hooks/use-dispensary-payment-methods';
import useUser from 'shared/hooks/use-user';
import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import { Nullish } from 'shared/utils/type-utils';

export type MxBankAccounts = (Pick<GqlBankAccount, 'id'> & {
  billingAddress?: Maybe<
    Pick<GqlBillingAddress, 'city' | 'country' | 'line1' | 'line2' | 'postalCode' | 'state'> & {
      _typename?: 'BillingAddress';
    }
  >;
  bankAccount?: Maybe<Pick<GqlBankDetails, 'bankName' | 'lastDigits'> & { _typename?: 'BankDetails' }>;
} & { _typename?: 'BankAccount' })[];

type UseDutchiePayUtils = {
  allowDutchiePayEnrollment: boolean;
  isDutchiePayEnabledForDispo: boolean;
  isEnrolledDutchiePay: boolean;
  bankAccounts?: MxBankAccounts | Nullish;
  billingAddress?:
    | Nullish
    | (Pick<GqlBillingAddress, 'city' | 'country' | 'line1' | 'line2' | 'postalCode' | 'state'> & {
        _typename?: 'BillingAddress' | undefined;
      });
};

export const useDutchiePay = (): UseDutchiePayUtils => {
  const User = useUser();
  const DutchiePayEnrollment = useDutchiePayEnrollment();

  const paymentOptions = useDispensaryPaymentMethods();

  const { data } = useGetEnrollmentStatusQuery({
    variables: { entityId: User.id, entityType: EntityTypes.user },
    skip: !User.exists && !User.hasExternalUser,
  });

  const enrollmentStatus = data?.getEnrollmentStatus;
  DutchiePayEnrollment.enrollmentStatusReceived(enrollmentStatus);
  const isEnrolledDutchiePay = enrollmentStatus?.enrollment?.status === StatusTypes.statusEnrolled;

  const bankAccounts = enrollmentStatus?.bankAccounts ?? [];

  const firstAccount = bankAccounts[0] ?? [];

  const { billingAddress } = firstAccount;

  const isDutchiePayEnabledForDispo = _.some(paymentOptions, { value: 'dutchiePay' });

  const allowDutchiePayEnrollment = isDutchiePayEnabledForDispo && !isEnrolledDutchiePay;

  return {
    allowDutchiePayEnrollment,
    isDutchiePayEnabledForDispo,
    isEnrolledDutchiePay,
    bankAccounts,
    billingAddress,
  };
};
